/**
 * Registrazione dello schermo, lato browser.
 *
 * Parte spenta e resta spenta finché l'interruttore non viene acceso dall'amministrazione,
 * cosa che a sua volta richiede i presupposti dell'art. 4 dello Statuto dei lavoratori.
 * Il codice di rrweb non viene nemmeno scaricato se l'interruttore è spento: è un
 * `import()` dinamico, quindi Vite lo mette in un file a parte che il browser chiede solo
 * quando serve davvero.
 *
 * Quello che finisce nella registrazione:
 *  - la forma della pagina (quali riquadri, dove, di che dimensione);
 *  - i movimenti e i clic;
 *  - **nessun testo**: `maskTextSelector: '*'` sostituisce ogni carattere con un asterisco,
 *    `maskAllInputs` fa lo stesso con quello che si scrive. Si vede che l'operatore ha
 *    compilato il campo «codice fiscale», non quale codice fiscale.
 *
 * Quello che succede prima di uscire dal browser:
 *  - gli eventi si accumulano trenta secondi;
 *  - il blocco viene cifrato con AES-256-GCM, con una chiave che vive solo per quella
 *    registrazione e che il server conserva a sua volta cifrata;
 *  - il blocco cifrato va diritto su Object Storage con un URL prefirmato. L'API non lo
 *    vede passare: conosce solo quanti blocchi ci sono e quanto pesano.
 */

type Stato = {
  continua: boolean
  assistenza: boolean
  giorni_conservazione: number
  avvertenza: string | null
}

type Apertura = { id: string; chiave: string; scade_fra_giorni: number }

const OGNI_QUANTO_SI_MANDA = 30_000     // un blocco ogni trenta secondi
const OGNI_QUANTO_SI_RIPARTE = 120_000  // istantanea completa ogni due minuti

export type Registrazione = {
  id: string
  modalita: 'continua' | 'assistenza'
  scadeFraGiorni: number
  ferma: () => Promise<void>
}

/** Chi sta guardando deve poterlo sapere: l'interfaccia ascolta questo. */
type Ascoltatore = (r: Registrazione | null) => void
const ascoltatori = new Set<Ascoltatore>()
let corrente: Registrazione | null = null

export function inCorso() { return corrente }
export function osserva(f: Ascoltatore) {
  ascoltatori.add(f)
  return () => { ascoltatori.delete(f) }
}
function annuncia(r: Registrazione | null) {
  corrente = r
  for (const f of ascoltatori) f(r)
}

export async function leggiStato(): Promise<Stato> {
  const risposta = await fetch('/api/registrazioni/stato', { credentials: 'include' })
  if (!risposta.ok) throw new Error('stato non disponibile')
  return risposta.json() as Promise<Stato>
}

/**
 * Avvia una registrazione. Torna `null` se l'interruttore è spento o se qualcosa non va:
 * una registrazione che non riesce a partire non deve mai impedire di lavorare.
 */
export async function avvia(modalita: 'continua' | 'assistenza'): Promise<Registrazione | null> {
  if (corrente) return corrente
  try {
    const risposta = await fetch('/api/registrazioni', {
      method: 'POST', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ modalita }),
    })
    if (!risposta.ok) return null
    const apertura = (await risposta.json()) as Apertura

    const { record } = await import('rrweb')
    const chiave = await importaChiave(apertura.chiave)

    let coda: unknown[] = []
    let numero = 0
    let chiusa = false
    let inVolo = false
    let nascostaIgnorata = false

    /**
     * Manda quello che c'è in coda. Se fallisce, i pezzi tornano in coda e riprova al giro
     * dopo. Un invio per volta: il battito e l'uscita dalla pagina possono chiamare insieme,
     * e due invii sovrapposti si darebbero lo stesso numero di blocco.
     */
    const manda = async () => {
      if (chiusa || inVolo || !coda.length) return
      // A pagina nascosta i pezzi si accumulano e non partono: un invio ogni trenta secondi da
      // una scheda che nessuno guarda riaccenderebbe il database serverless per una finestra
      // intera, ed e' il battito che la fascia oraria toglie (SIC-25). Il tetto sulla coda
      // resta quello di sempre, quindi una pausa lunga non mangia la memoria del browser:
      // perde gli eventi piu' vecchi, che l'istantanea completa rimette in sesto.
      if (document.hidden && !nascostaIgnorata) return
      inVolo = true
      const blocco = coda
      coda = []
      try {
        const testo = new TextEncoder().encode(JSON.stringify(blocco))
        const iv = crypto.getRandomValues(new Uint8Array(12))
        const cifrato = new Uint8Array(
          await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, chiave, testo))
        const corpo = new Uint8Array(iv.length + cifrato.length)
        corpo.set(iv, 0)
        corpo.set(cifrato, iv.length)

        const mio = numero++
        const prenota = await fetch(`/api/registrazioni/${apertura.id}/pezzo`, {
          method: 'POST', credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ numero: mio, dimensione: corpo.byteLength }),
        })
        if (!prenota.ok) throw new Error('prenotazione rifiutata')
        const { url } = (await prenota.json()) as { url: string }
        const messo = await fetch(url, {
          method: 'PUT',
          headers: { 'content-type': 'application/octet-stream', 'x-amz-server-side-encryption': 'AES256' },
          body: corpo,
        })
        if (!messo.ok) throw new Error(`archivio ha risposto ${messo.status}`)
      } catch {
        // Rimettiamo davanti quello che non è partito: al giro dopo riparte da lì.
        coda = blocco.concat(coda)
        numero -= 1
      } finally {
        inVolo = false
      }
    }

    /**
     * La stessa spedizione, senza il freno della pagina nascosta: serve all'uscita e alla
     * chiusura, che sono eventi unici e non ripetuti.
     */
    const mandaComunque = async () => {
      nascostaIgnorata = true
      try { await manda() } finally { nascostaIgnorata = false }
    }

    const smetti = record({
      emit(evento) {
        coda.push(evento)
        // Se l'archivio non risponde per molto la coda non deve mangiarsi la memoria del
        // browser: si buttano gli eventi più vecchi. Quel pezzo di filmato si perde, ma
        // l'istantanea completa che arriva ogni due minuti rimette tutto in sesto.
        if (coda.length > 20_000) coda.splice(0, coda.length - 20_000)
      },
      checkoutEveryNms: OGNI_QUANTO_SI_RIPARTE,
      maskAllInputs: true,
      maskTextSelector: '*',
      maskInputOptions: { password: true, email: true, tel: true, text: true, textarea: true, number: true, date: true, select: true },
      blockClass: 'niente-registrazione',
      ignoreClass: 'niente-registrazione',
      recordCanvas: false,
      collectFonts: false,
      inlineImages: false,
      slimDOMOptions: true,
      sampling: { mousemove: 100, scroll: 150, input: 'last' },
      errorHandler: () => true,   // un errore del registratore non deve fermare l'applicazione
    })

    const battito = window.setInterval(() => { void manda() }, OGNI_QUANTO_SI_MANDA)
    // Al ritorno in primo piano si manda subito quello che si e' accumulato: e' il momento in
    // cui il ritardo si recupera senza costare niente in piu', perche' la persona e' tornata.
    const alRitorno = () => { if (!document.hidden) void manda() }
    // Uscire dalla pagina e' l'ultima occasione. Qui `document.hidden` e' gia' vero, quindi
    // `manda()` da solo non partirebbe: si forza, perche' un invio all'uscita e' uno solo e non
    // un battito, e senza di lui l'ultimo mezzo minuto di registrazione andrebbe perso.
    const allUscita = () => { void mandaComunque() }
    document.addEventListener('visibilitychange', alRitorno)
    window.addEventListener('pagehide', allUscita)

    const registrazione: Registrazione = {
      id: apertura.id,
      modalita,
      scadeFraGiorni: apertura.scade_fra_giorni,
      ferma: async () => {
        if (chiusa) return
        smetti?.()
        window.clearInterval(battito)
        document.removeEventListener('visibilitychange', alRitorno)
        window.removeEventListener('pagehide', allUscita)
        await mandaComunque()
        chiusa = true
        await fetch(`/api/registrazioni/${apertura.id}/chiudi`, { method: 'POST', credentials: 'include' })
          .catch(() => undefined)
        annuncia(null)
      },
    }
    annuncia(registrazione)
    return registrazione
  } catch {
    return null
  }
}

async function importaChiave(base64: string): Promise<CryptoKey> {
  const grezza = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
  return crypto.subtle.importKey('raw', grezza, 'AES-GCM', false, ['encrypt'])
}

/** Il contrario, per chi rivede una registrazione (Referente privacy o DPO, con approvazione). */
export async function decifraBlocco(chiaveBase64: string, dati: ArrayBuffer): Promise<unknown[]> {
  const grezza = Uint8Array.from(atob(chiaveBase64), (c) => c.charCodeAt(0))
  const chiave = await crypto.subtle.importKey('raw', grezza, 'AES-GCM', false, ['decrypt'])
  const tutto = new Uint8Array(dati)
  const iv = tutto.slice(0, 12)
  const corpo = tutto.slice(12)
  const chiaro = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, chiave, corpo)
  return JSON.parse(new TextDecoder().decode(chiaro)) as unknown[]
}
